import ellipsis from '@/presentation/utils/ellipsis';
import { Vault } from 'nftx.js';
import { useState } from 'react';
import Price from '../../elements/Price';
import { useFormatter } from 'use-intl';
import Icon, { Icons } from '../../elements/Icon';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useRole,
  useClick,
  useInteractions,
  FloatingFocusManager,
} from '@floating-ui/react';

const VaultSwitcher = ({
  vaults = [],
  selected,
  setSelected,
  showAddress = true,
}: {
  vaults?: Vault[];
  selected?: Vault;
  setSelected: (v?: Vault) => void;
  showAddress?: boolean;
}) => {
  const [showVaults, setShowVaults] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-start',
    open: showVaults,
    onOpenChange: setShowVaults,
    middleware: [
      offset({ crossAxis: -16, mainAxis: 8 }),
      flip({ fallbackAxisSideDirection: 'end' }),
      shift(),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const { number } = useFormatter();

  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps()}
        type="button"
        className="bg-gray-50 border border-gray-300 px-2 py-2 rounded-md inline-flex items-center space-x-2"
      >
        <span className="text-base font-medium">
          {selected ? selected?.token.symbol : 'All Vaults'}
        </span>
        {showAddress && (
          <span className="text-sm font-normal text-gray-500">
            {selected ? ellipsis(selected?.token.id) : ''}
          </span>
        )}
        <Icon name={Icons.CHEVRON_DOWN} size={16} className="text-gray-500" />
      </button>
      {showVaults && (
        <FloatingFocusManager context={context} modal={false}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="shadow-lg rounded-lg bg-gray-50 whitespace-nowrap z-10 border border-gray-300 overflow-hidden"
          >
            <table className="w-full text-right text-sm">
              <thead className="uppercase">
                <tr>
                  <th className="py-2 px-2 bg-gray-100" />
                  {['vault', 'price', 'items'].map((k) => (
                    <th
                      key={k}
                      className={`bg-gray-100 text-gray-500 py-2 font-normal text-xs ${
                        k === 'vault' ? 'text-left pr-8' : 'px-4'
                      }`}
                    >
                      {k}
                    </th>
                  ))}
                  <th className="px-2 bg-gray-100" />
                </tr>
              </thead>
              <tbody>
                {vaults.map((v) => {
                  return (
                    <tr
                      key={v.vaultId}
                      onClick={() => setSelected(v)}
                      className="hover:bg-gray-100 cursor-pointer"
                    >
                      <td className="px-3 py-2 text-right">
                        {v.vaultId === selected?.vaultId ? (
                          <span className="h-2 w-2 rounded-full bg-green-500 block" />
                        ) : null}
                      </td>
                      <td className="pr-8 py-2 text-left">
                        <h4 className="font-medium text-base leading-none">
                          {v.token.symbol}
                        </h4>
                        <span className="text-xs font-normal text-gray-500">
                          {ellipsis(v.token.id)}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <Price value={v.vTokenToEth} />
                      </td>
                      <td className="px-4 py-2">{number(v.totalHoldings)}</td>
                      <td />
                    </tr>
                  );
                })}
                <tr>
                  <td colSpan={5} className="py-2 text-center">
                    <button
                      onClick={() => setSelected(undefined)}
                      type="button"
                      className="underline hover:no-underline"
                    >
                      {'All Vaults'}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </FloatingFocusManager>
      )}
    </>
  );
};

export default VaultSwitcher;
